import React, { Component } from 'react'
import { graphql } from '@apollo/client/react/hoc'
import MenuItem from '@mui/material/MenuItem'
import MUISelect from '@mui/material/Select'
import gql from 'graphql-tag'
import compose from 'lodash/flowRight'
import omit from 'lodash/omit'
import { Subject } from 'rxjs/Subject'
import styled from 'styled-components'

import {
  ProjectStatusEnum,
  StatusEnumType,
} from 'shared/common/global-types.runtime'
import { COLORS } from '~/common/globalStyles'
import {
  taskStatusEnum,
  actionItemEnum,
  projectStatusEnum,
} from '~/common/types/enum-dictionaries'
import {
  ChangeStatusDropdownTaskFragment,
  ChangeStatusDropdownTemplateFragment,
  ChangeStatusDropdownUpdateNodeStatusMutationFn,
  ChangeStatusDropdownUpdateNodeStatusMutationVariables,
  ChangeStatusTaskReturnFragment,
  MilestoneType,
  StatusObjectEnumType,
  TaskType,
  UserProjectOrganizationTypeEnumType,
} from '~/common/types/graphql'
import SelectStatusOptionRow from '~/views/components/SelectStatusOptionRow'
import dateHelpers from '~/utils/dates'
import { TASK_STATUS } from 'shared/common/enums'
import {
  withCurrentUser,
  CurrentUser,
} from '~/views/contexts/CurrentUserContext'

const MenuProps = {
  PaperProps: {
    style: {
      width: 155,
    },
  },
}

type SelectTaskCompleteProps = Omit<
  React.ComponentProps<typeof MUISelect>,
  'variant'
> & {
  drawerVersion?: boolean
  wide?: boolean
  completedAt?: string | Date | null
  show?: boolean
  variant?: 'standard'
}

const SelectTaskComplete = styled(
  ({ drawerVersion, ...rest }: SelectTaskCompleteProps) => {
    let iconModifier = drawerVersion ? 'drawer' : ''
    if (rest.disabled) iconModifier = 'disabled'

    return (
      <MUISelect
        variant="standard"
        classes={{
          select: 'select',
          icon: `icon ${iconModifier}`,
        }}
        {...omit(rest, ['show', 'completedAt'])}
      />
    )
  },
)(({ wide, completedAt, show, theme }) => {
  const completedLabel = show
    ? `Completed on ${dateHelpers.prettyDate((completedAt as Date | null) ?? null)}`
    : ''

  return `
    width: ${wide ? '100%' : 'inherit'};

    &:after {
      content: ${completedLabel};
      position: absolute;
      top: 10px;
      right: 54px;
      font-size: 10px;
      font-style: italic;
      color: white;
      pointer-events: none;
    }

    & .select {
      padding: 0px !important;
    }

    & .icon {
      color: ${COLORS.white};
      top: 0px;
      height: 100%;

      &.drawer {
        font-size: 33px;
        right: ${theme.spacing(3)};
      }

      &.disabled {
        visibility: hidden;
      }
    }
  `
})

interface StatusObservableValueType {
  currentStatus: StatusEnumType
  status: StatusEnumType
  id: TaskType['id']
  stuckMessage: string
  shouldWarnUpdateStatus: TaskType['shouldWarnUpdateStatus']
}

type StatusObservableType = Subject<StatusObservableValueType>

interface ChangeStatusDropdownProps {
  projectId: string
  taskId: string
  status: StatusEnumType | ProjectStatusEnum
  completedAt?: string
  disabled: boolean
  drawerVersion?: boolean
  isActionItem?: boolean
  signOffRequired: boolean
  isEventTask: boolean
  currentUserProjectContext: UserProjectOrganizationTypeEnumType
  currentUser: CurrentUser
  dependency: TaskType | MilestoneType
  hardDependency: boolean
  templateId: string
  isPlanView: boolean
  shouldWarnUpdateStatus: boolean
  isDraggingTask: boolean
  handleSendSignOffClick?(): void
  'data-testid'?: string
}

interface HOCProps {
  controlStatus?: (
    statusObservable: StatusObservableType,
    updateStatus: ChangeStatusDropdownUpdateNodeStatusMutationFn,
  ) => void
  updateStatus: ChangeStatusDropdownUpdateNodeStatusMutationFn
}

type Props = HOCProps & ChangeStatusDropdownProps

interface StateProps {
  isSaving: boolean
  isOpen: boolean
}

interface FragmentProps {
  taskReturn: ChangeStatusTaskReturnFragment
  task: ChangeStatusDropdownTaskFragment
  template: ChangeStatusDropdownTemplateFragment
}

class ChangeStatusDropdown extends Component<Props, StateProps> {
  statusObservable = new Subject()
  override state = {
    isSaving: false,
    isOpen: false,
  }
  static fragments: FragmentDocs<FragmentProps>

  override componentDidMount() {
    if (this.props.controlStatus) {
      this.props.controlStatus(
        this.statusObservable as StatusObservableType,
        this.props.updateStatus,
      )
    }
  }

  override componentWillUnmount() {
    this.statusObservable.unsubscribe()
  }

  updateStatus = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      taskId,
      projectId,
      status,
      controlStatus,
      shouldWarnUpdateStatus,
      updateStatus,
    } = this.props

    const nodeId = taskId || projectId
    const nodeType = projectId
      ? StatusObjectEnumType.Project
      : StatusObjectEnumType.Task

    if (this.state.isSaving || status === event.target.value) return
    this.setState({ isSaving: false }, () => {
      if (controlStatus) {
        // If there's a controlStatus prop, treat component like it's controlled
        this.statusObservable.next({
          currentStatus: status,
          status: event.target.value,
          id: nodeId,
          stuckMessage: '',
          shouldWarnUpdateStatus: shouldWarnUpdateStatus,
          objectType: nodeType,
        })
      } else {
        // If no controlStatus then control itself
        updateStatus({
          variables: {
            id: taskId || projectId,
            status: event.target.value as StatusEnumType,
            objectType: nodeType,
          },
        })
      }
    })
  }

  override render() {
    const {
      projectId,
      status: taskStatus,
      completedAt,
      disabled,
      signOffRequired,
      drawerVersion,
      currentUserProjectContext,
      isEventTask,
      dependency,
      hardDependency,
      isActionItem,
      isDraggingTask,
      currentUser,
      'data-testid': dataTestId,
    } = this.props

    const signOffFilter = (status: { value: string }) =>
      !signOffRequired && status.value === 'SIGN_OFF'

    const notApplicableFilter = (status: { value: string }) =>
      currentUserProjectContext !== 'PROVIDER' &&
      status.value === 'NOT_APPLICABLE'

    const eventTaskFilter = (status: { value: string }) =>
      (!isEventTask &&
        (status.value === 'NOT_SCHEDULED' || status.value === 'SCHEDULED')) ||
      (isEventTask &&
        status.value !== 'NOT_SCHEDULED' &&
        status.value !== 'SCHEDULED' &&
        status.value !== 'DONE' &&
        status.value !== 'NOT_APPLICABLE')

    const hardDependencyFilter = (status: { value: string }) =>
      ((hardDependency && dependency && !dependency.completed) ||
        taskStatus === 'NOT_APPLICABLE') &&
      ((!isEventTask &&
        !['NOT_STARTED', 'NOT_APPLICABLE'].includes(status.value)) ||
        (isEventTask &&
          !['NOT_SCHEDULED', 'SCHEDULED', 'NOT_APPLICABLE'].includes(
            status.value,
          )))

    const statusEnum = projectId ? projectStatusEnum : taskStatusEnum
    const filteredStatusEnum = projectId
      ? projectStatusEnum
      : isActionItem
        ? actionItemEnum
        : taskStatusEnum.filter((status) => {
            return signOffFilter(status) ||
              notApplicableFilter(status) ||
              eventTaskFilter(status) ||
              hardDependencyFilter(status)
              ? false
              : true
          })

    const isTaskDone = taskStatus === TASK_STATUS.done
    const isTask = !projectId && !isActionItem

    if (isDraggingTask && this.state.isOpen) this.setState({ isOpen: false })
    if (!taskStatus) return null

    const restrictSignOffStatus = (statusValue: string) => {
      if (statusValue !== TASK_STATUS.done) return false

      return (
        !(currentUser?.isAdmin() || currentUser?.isManager()) && signOffRequired
      )
    }

    return (
      <SelectTaskComplete
        data-testid={dataTestId || 'select-dropdown'}
        open={this.state.isOpen && !isDraggingTask}
        onClose={() => this.setState({ isOpen: false })}
        onOpen={() => this.setState({ isOpen: true })}
        value={taskStatus}
        completedAt={completedAt}
        show={Boolean(isTaskDone && completedAt)}
        onChange={this.updateStatus as SelectTaskCompleteProps['onChange']}
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation()
          event.preventDefault()
        }}
        disableUnderline
        renderValue={(selected) => {
          const selectedStatus = statusEnum.find(
            (enumStatus) => enumStatus.value === selected,
          )
          return (
            <SelectStatusOptionRow
              renderValue
              drawerVersion={drawerVersion}
              statusEnum={selectedStatus}
              data-testid="task-status-selected-option"
            />
          )
        }}
        disabled={disabled}
        drawerVersion={drawerVersion}
        MenuProps={!drawerVersion && isTask ? MenuProps : {}}
      >
        {filteredStatusEnum.map((currentStatusEnum) => (
          <MenuItem
            data-testid={`menu-item-${currentStatusEnum.value}`}
            key={currentStatusEnum.value}
            value={currentStatusEnum.value}
            disabled={restrictSignOffStatus(currentStatusEnum.value)}
          >
            <SelectStatusOptionRow statusEnum={currentStatusEnum} />
          </MenuItem>
        ))}
        {!isEventTask && dependency && !dependency.completed && (
          <MenuItem dense disabled>
            Dependency not met
          </MenuItem>
        )}
      </SelectTaskComplete>
    )
  }
}

ChangeStatusDropdown.fragments = {
  taskReturn: gql`
    fragment ChangeStatus_TaskReturn on TaskReturnType {
      task {
        id
        status
      }
    }
  `,
  task: gql`
    fragment ChangeStatusDropdown_Task on TaskType {
      id
      dependentTasks {
        id
      }
      milestone {
        id
        sortOrder
        dependentTasks {
          id
        }
      }
    }
  `,
  template: gql`
    fragment ChangeStatusDropdown_Template on TemplateType {
      milestones {
        id
        sortOrder
        tasks {
          id
        }
        __typename
      }
      __typename
    }
  `,
}

const updateNodeStatus = graphql(
  gql`
    mutation ChangeStatusDropdown_UpdateNodeStatus(
      $id: ID!
      $status: StatusEnumType!
      $objectType: StatusObjectEnumType!
    ) {
      updateObjectStatus(
        input: { id: $id, status: $status, objectType: $objectType }
      ) {
        resolveObject {
          ... on ProjectType {
            id
            status
          }
          ... on TaskType {
            id
            status
            completedAt
            completed
            project {
              id
              startOn
              endOn
              completedAt
              forecastedEndOn
              stats {
                totalTasks
                totalDoneTasks
                totalAssignedTasks
                totalTasksWithUnestimatedHoursCount
                totalActualHours
                totalInternalEstimatedHours
              }
              profitabilitySummary {
                forecastedCost
                budgetedCost
              }
            }
          }
        }
      }
    }
  `,
  {
    props: ({ mutate }) => {
      return {
        updateStatus: async (
          variables: ChangeStatusDropdownUpdateNodeStatusMutationVariables,
        ) => {
          if (!mutate) {
            throw new Error('Mutation function not available')
          }

          const result = await mutate({
            variables: { ...variables },
            refetchQueries: ['ProjectLayout_Project'],
          })

          // Check for GraphQL errors and throw if present
          if (result?.errors?.length) {
            throw new Error(
              result.errors[0]?.message || 'Failed to update status',
            )
          }

          if (!(result?.data as any)?.updateObjectStatus?.resolveObject) {
            throw new Error('Failed to update status')
          }

          return result
        },
      }
    },
  },
)

export default compose(withCurrentUser, updateNodeStatus)(ChangeStatusDropdown)
